/**
 * Memory for the public diagnostic (/ and /try).
 *
 * The diagnostic lived entirely in `useState`, so the site forgot a visitor the
 * moment they refreshed or came back: a returning visitor got the same cold
 * "Which exam are you taking?" screen as a first-timer, with their result gone.
 * This persists just the RESULT SUMMARY (never the answer sheet) so the intro can
 * greet them with their own number and point at their weakest area.
 *
 * Stored under a versioned key with a 90-day TTL via `localStorageWithTTL`, so
 * the boot-time sweep in App.tsx collects it once it goes stale, and a future
 * shape change bumps the key rather than trying to migrate anonymous data.
 *
 * Internal traffic (the founder's own `?internal=1` device) is never written:
 * the same suppression that keeps our testing out of GA4 keeps it out of here,
 * so QA passes don't hand us a "welcome back" the real funnel never saw.
 */

import { getItemWithTTL, removeItemWithTTL, setItemWithTTL, TTL } from '@/utils/localStorageWithTTL';
import { isInternalTraffic } from '@/utils/internalTraffic';

/** Versioned: bump the suffix rather than migrating anonymous visitors' data. */
export const DIAGNOSTIC_MEMORY_KEY = 'swtpa_diag_v1';

export type DiagnosticExamType = 'LBSW' | 'LMSW' | 'LCSW';

export interface StoredDiagnostic {
  /** The exam they picked, so a returning visitor starts pre-selected. */
  examType: DiagnosticExamType | null;
  /** Questions answered correctly on their last run. */
  correctCount: number;
  /** Estimated readiness percentage (0-100) from that run. */
  readiness: number;
  /** Content areas they missed, strongest signal first-listed. */
  weak: string[];
  /** Epoch ms the run finished. */
  completedAt: number;
  /**
   * Whether the visitor traded their email for the reveal. The score is HELD
   * until this is true: a returning visitor who never captured is sent back to
   * the held reveal (not the report), so they are not re-gated once they have.
   */
  captured: boolean;
  /**
   * The signed Pass Plan download token minted by captureLead on capture. Present
   * once captured (absent if the backend had no secret configured), so a returning
   * visitor can re-download the packet without re-entering their email.
   */
  downloadToken?: string;
}

const EXAM_TYPES: readonly string[] = ['LBSW', 'LMSW', 'LCSW'];

/** The diagnostic is 6 questions; bounds the stored score and weak-area list. */
export const DIAGNOSTIC_QUESTION_TOTAL = 6;
const MAX_DIAGNOSTIC_QUESTIONS = DIAGNOSTIC_QUESTION_TOTAL;

/**
 * localStorage is user-writable and survives deploys, so anything read back is
 * untrusted input: a partial or hand-edited record must read as "no memory"
 * rather than render `undefined/6` at a visitor.
 */
function isStoredDiagnostic(value: unknown): value is StoredDiagnostic {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  const examTypeOk = v.examType === null || (typeof v.examType === 'string' && EXAM_TYPES.includes(v.examType));
  // Finite isn't enough: a hand-edited record must not be able to render "-5/6"
  // or "999% ready" at a visitor, so every number is range-checked too.
  const isInt = (n: unknown, min: number, max: number): boolean =>
    typeof n === 'number' && Number.isInteger(n) && n >= min && n <= max;
  // `captured`/`downloadToken` are optional on disk: records written before the
  // email gate existed (v1) simply lack them, and a missing capture reads as
  // "not captured yet" rather than discarding an otherwise valid score.
  const capturedOk = v.captured === undefined || typeof v.captured === 'boolean';
  const tokenOk = v.downloadToken === undefined || (typeof v.downloadToken === 'string' && v.downloadToken.length <= 512);
  return (
    examTypeOk &&
    isInt(v.correctCount, 0, MAX_DIAGNOSTIC_QUESTIONS) &&
    isInt(v.readiness, 0, 100) &&
    Array.isArray(v.weak) &&
    v.weak.length <= MAX_DIAGNOSTIC_QUESTIONS &&
    v.weak.every((w) => typeof w === 'string' && w.length > 0 && w.length <= 120) &&
    isInt(v.completedAt, 1, Number.MAX_SAFE_INTEGER) &&
    capturedOk &&
    tokenOk
  );
}

/** The visitor's last diagnostic result, or null when there is none (or it rotted). */
export function readDiagnosticResult(): StoredDiagnostic | null {
  const raw = getItemWithTTL<unknown>(DIAGNOSTIC_MEMORY_KEY);
  if (!isStoredDiagnostic(raw)) return null;
  // Normalize the optional capture flag so callers get a definite boolean.
  return { ...raw, captured: raw.captured === true };
}

/**
 * Persist a completed run. No-op on internal traffic (see the module note) and
 * on any storage failure (Safari private mode), because remembering a result is
 * never worth breaking the results screen over.
 */
export function saveDiagnosticResult(result: Omit<StoredDiagnostic, 'completedAt'> & { completedAt?: number }): void {
  if (isInternalTraffic()) return;
  const record: StoredDiagnostic = {
    examType: result.examType,
    correctCount: result.correctCount,
    readiness: result.readiness,
    weak: [...result.weak],
    completedAt: result.completedAt ?? Date.now(),
    captured: result.captured,
    ...(result.downloadToken ? { downloadToken: result.downloadToken } : {}),
  };
  setItemWithTTL(DIAGNOSTIC_MEMORY_KEY, record, TTL.THREE_MONTHS);
}

/**
 * Record that the visitor traded their email: flips `captured` on the remembered
 * run and stores the download token, WITHOUT touching the score. A no-op on
 * internal traffic (nothing was persisted) or when there is no prior result.
 */
export function markDiagnosticCaptured(downloadToken?: string): void {
  if (isInternalTraffic()) return;
  const current = readDiagnosticResult();
  if (!current) return;
  setItemWithTTL(
    DIAGNOSTIC_MEMORY_KEY,
    { ...current, captured: true, ...(downloadToken ? { downloadToken } : {}) },
    TTL.THREE_MONTHS,
  );
}

/** Forget the last result — what "Retake the diagnostic" does. */
export function clearDiagnosticResult(): void {
  removeItemWithTTL(DIAGNOSTIC_MEMORY_KEY);
}

/**
 * A run in progress, kept in sessionStorage (this tab only): iOS Safari discards a
 * backgrounded tab and reloads it, which used to drop a visitor mid-quiz back to
 * the start. Cleared when the run completes (the result above takes over) or is retaken.
 */
export const DIAGNOSTIC_PROGRESS_KEY = 'swtpa_diag_progress_v1';

export interface DiagnosticProgress {
  examType: DiagnosticExamType | null;
  answers: Record<number, string>;
  /** The question on screen, by id, so a prefilled run (one fewer question) resumes correctly. */
  questionId: number;
  /** The pick awaiting Continue, or null. */
  picked: string | null;
}

const isLetter = (v: unknown): v is string => typeof v === 'string' && /^[A-D]$/.test(v);

export function readDiagnosticProgress(): DiagnosticProgress | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(DIAGNOSTIC_PROGRESS_KEY) ?? 'null') as Record<string, unknown> | null;
    if (typeof v !== 'object' || v === null) return null;
    const answersOk =
      typeof v.answers === 'object' && v.answers !== null && Object.values(v.answers).every(isLetter);
    const examOk = v.examType === null || (typeof v.examType === 'string' && EXAM_TYPES.includes(v.examType));
    if (!answersOk || !examOk || !Number.isInteger(v.questionId) || !(v.picked === null || isLetter(v.picked))) return null;
    return v as unknown as DiagnosticProgress;
  } catch {
    return null;
  }
}

export function saveDiagnosticProgress(progress: DiagnosticProgress): void {
  try {
    sessionStorage.setItem(DIAGNOSTIC_PROGRESS_KEY, JSON.stringify(progress));
  } catch {
    /* private mode / quota: resuming is best effort */
  }
}

export function clearDiagnosticProgress(): void {
  try {
    sessionStorage.removeItem(DIAGNOSTIC_PROGRESS_KEY);
  } catch {
    /* storage blocked */
  }
}
